import * as React from "react";
import styled, { css } from "styled-components";
import { E2EIdentifier } from "../../../core/e2e/E2EIdentifier";
import { iPadPortraitOnly } from "../../../ui/_utils/styleUtils";
import { CloseButton } from "../common/CloseButton";
import { BetSlider } from "./BetSlider";

export interface IBetInCoinsProps {
  className?: string;
  title: string;
  coinValueTitle: string;
  coinValues: number[];
  selectedCoinValue: number;
  betLevelTitle: string;
  betLevels: number[];
  selectedBetLevel: number;
  formatter(cash: number): string;
  onCoinValueChanged(value: number): void;
  onBetLevelChanged(level: number): void;
  onClose(): void;
}

function View({
  className,
  title,
  coinValueTitle,
  coinValues,
  selectedCoinValue,
  betLevelTitle,
  betLevels,
  selectedBetLevel,
  formatter,
  onCoinValueChanged,
  onBetLevelChanged,
  onClose
}: IBetInCoinsProps) {
  const shouldRenderBetLevelSlider = !(
    betLevels.length === 1 && betLevels[0] === selectedBetLevel
  );

  return (
    <div className={className} data-e2e-id={E2EIdentifier.BET_VIEW_IN_COINS}>
      <CloseButton onClose={onClose} />
      <h1>{title}</h1>
      <div>
        <section>
          <div>
            <h1>{coinValueTitle}</h1>
            <h2>{formatter(selectedCoinValue)}</h2>
          </div>
          <BetSlider
            values={coinValues}
            value={selectedCoinValue}
            onValueUpdate={onCoinValueChanged}
            formatter={formatter}
          />
        </section>

        {shouldRenderBetLevelSlider && (
          <section>
            <div>
              <h1>{betLevelTitle}</h1>
              <h2>{selectedBetLevel}</h2>
            </div>
            <BetSlider
              values={betLevels}
              value={selectedBetLevel}
              onValueUpdate={onBetLevelChanged}
            />
          </section>
        )}
      </div>
    </div>
  );
}

export const BetInCoins = styled(View)`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  padding: 2em;
  overflow: auto;

  ${CloseButton} {
    position: fixed;
    top: 1em;
    right: 1em;
  }

  > h1 {
    display: none;
    font-size: 1.3em;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    margin: 0;
    padding: 0;
    text-align: center;
  }

  /* coin value and bet level containers */
  > div {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-around; /* Fallback value for IE11 */
    justify-content: space-evenly;
    align-items: center;
    margin-bottom: 1.4em;

    > section {
      display: flex;
      flex-flow: column;
      font-size: 0.9em;
      width: 100%;

      /* title + value container */
      > div {
        display: flex;
        flex-flow: column;
        align-items: center;

        /* title */
        > h1 {
          font-size: 1.2em;
          text-transform: uppercase;
          font-weight: 400;

          margin: 0;
          margin-bottom: 0.2em;
          padding: 0;

          touch-action: none;
          pointer-events: none;
          user-select: none;
        }

        /* value */
        > h2 {
          font-size: 1.5em;
          margin: 0;
          margin-bottom: -0.5em;
          padding: 0;
          font-weight: bold;

          touch-action: none;
          pointer-events: none;
          user-select: none;
        }
      }
    }
  }

  ${iPadPortraitOnly(css`
    > div > section {
      width: 85%;
    }
  `)}

  @media (orientation: landscape) {
    > div > section {
      width: 75%;
    }
  }

  @media (orientation: landscape) and (min-device-width: 1200px) {
    > h1 {
      display: block;
    }
    > div > section {
      width: 50%;
    }
  }
`;
